import { NavLink } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import './Sidebar.css';

/* Using inline SVGs instead of react-icons to avoid adding a new dependency */
const icons = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  tracks: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h4M10 5l7 7-7 7M10 12h11" />
    </svg>
  ),
  roadmaps: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l4-8 4 4 4-6 4 5" />
      <circle cx="3" cy="17" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  myCourses: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  addCourse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="M10 10l2-2 2 2M12 8v5" />
    </svg>
  ),
  upgrade: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  ),
  collapse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  ),
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useContext(AuthContext);
  const isPro = user?.subscription?.status === "ACTIVE";

  const NAV_SECTIONS = [
    {
      label: "LEARN",
      links: [
        { to: "/dashboard", label: "Dashboard", icon: icons.dashboard },
        { to: "/tracks", label: "Tracks", icon: icons.tracks, badge: isPro ? "PRO" : "FREE" },
        { to: "/my-courses", label: "My Courses", icon: icons.myCourses },
        { to: "/add-course", label: "Add Course", icon: icons.addCourse },
        { to: "/roadmaps", label: "Roadmaps", icon: icons.roadmaps },
      ],
    },
    {
      label: "ACCOUNT",
      links: [
        {
          to: "/upgrade",
          label: isPro ? "Your Plan" : "Upgrade",
          icon: icons.upgrade,
          highlight: !isPro
        },
        { to: "/profile", label: "Profile", icon: icons.profile },
      ],
    },
    ...(user?.role === "admin" ? [{
      label: "ADMIN",
      links: [
        { to: "/admin", label: "Admin Panel", icon: icons.admin },
      ],
    }] : []),
  ];
  return (
    <aside className={`global-sidebar ${collapsed ? 'global-sidebar--collapsed' : ''}`}>

      {/* Toggle button */}
      <button
        className={`sidebar-toggle ${collapsed ? 'sidebar-toggle--collapsed' : ''}`}
        onClick={onToggle}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {icons.collapse}
      </button>

      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} className="sidebar-section">
            {!collapsed && (
              <span className="sidebar-section-label">{section.label}</span>
            )}
            <ul>
              {section.links.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === "/dashboard"}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'sidebar-link--active' : ''} ${link.highlight ? 'sidebar-link--highlight' : ''}`
                    }
                    title={collapsed ? link.label : undefined}
                  >
                    <span className="sidebar-link-icon">{link.icon}</span>
                    {!collapsed && (
                      <span className="sidebar-link-label">{link.label}</span>
                    )}
                    {!collapsed && link.badge && (
                      <span className="sidebar-badge">{link.badge}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}